/* eslint-disable react-native/no-color-literals */
import React, { useState } from "react";
import {
  Modal,
  Pressable,
  StyleSheet,
  TouchableOpacity,
  View,
  FlatList,
  ActivityIndicator,
} from "react-native";
import Text from "@components/CustomText";
import Cross from "@assets/svg/CrossIcon";
import colors from "@constants/color";

interface MissingItem {
  order_item_id: number;
  order_id: number;
  title: string;
  missing_quantity: number;
}

interface Props {
  visible: boolean;
  missingUnits: number;
  missingItems: MissingItem[];
  isSubmitting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const ItemSeparator = () => <View style={styles.separator} />;

const MissingItemsConfirmModal: React.FC<Props> = ({
  visible,
  missingUnits,
  missingItems,
  isSubmitting,
  onCancel,
  onConfirm,
}) => {
  const [showItems, setShowItems] = useState(false);
  const itemCount = missingItems.length;

  const summaryText = `⚠ ${itemCount} आइटम का ${missingUnits} क्वानिटी मिसिंग मार्क हो रहा है।`;
  const warningSubText =
    "ये आइटम नहीं मिला के रूप में दर्ज होंगे और पिक नहीं किए जाएंगे।";

  const renderItem = ({ item }: { item: MissingItem }) => (
    <View style={styles.row}>
      <View style={styles.rowInfo}>
        <Text variant="body_text_4" style={styles.itemTitle} numberOfLines={2}>
          {item.title}
        </Text>
        <Text variant="caption_2" style={styles.orderLabel}>
          Order #{item.order_id}
        </Text>
      </View>
      <Text variant="body_text_3" style={styles.missingQty}>
        ×{item.missing_quantity}
      </Text>
    </View>
  );

  return (
    <Modal
      animationType="fade"
      transparent={true}
      visible={visible}
      onRequestClose={onCancel}
      statusBarTranslucent
      navigationBarTranslucent
    >
      <Pressable style={styles.overlay} onPress={onCancel} />

      <View style={styles.centerWrap} pointerEvents="box-none">
        <View style={styles.card}>
          <Pressable style={styles.closeBtn} onPress={onCancel}>
            <Cross width={14} height={14} />
          </Pressable>

          <Text variant="title_20" style={styles.title}>
            Confirm pickup
          </Text>

          <View style={styles.warningBox}>
            <Text variant="body_text_2" style={styles.warningTitle}>
              {summaryText}
            </Text>
            <Text variant="caption_2" style={styles.warningSub}>
              {warningSubText}
            </Text>
          </View>

          {itemCount > 0 && (
            <>
              <TouchableOpacity
                style={styles.viewToggle}
                onPress={() => setShowItems((s) => !s)}
                activeOpacity={0.7}
              >
                <Text variant="body_text_3" style={styles.viewToggleText}>
                  {showItems ? "▲ छुपाएं" : `▸ आइटम देखें (${itemCount})`}
                </Text>
              </TouchableOpacity>

              {showItems && (
                <FlatList
                  data={missingItems}
                  keyExtractor={(item) => `${item.order_item_id}`}
                  renderItem={renderItem}
                  ItemSeparatorComponent={ItemSeparator}
                  showsVerticalScrollIndicator={false}
                  style={styles.list}
                />
              )}
            </>
          )}

          <View style={styles.footer}>
            <TouchableOpacity
              style={[styles.footerBtn, styles.cancelBtn]}
              onPress={onCancel}
              disabled={isSubmitting}
              activeOpacity={0.7}
            >
              <Text variant="body_text_2" style={styles.cancelText}>
                Cancel
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.footerBtn, styles.confirmBtn]}
              onPress={onConfirm}
              disabled={isSubmitting}
              activeOpacity={0.7}
            >
              {isSubmitting ? (
                <ActivityIndicator size="small" color={colors.white} />
              ) : (
                <Text variant="body_text_2" style={styles.confirmText}>
                  Confirm
                </Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFill,
    backgroundColor: "rgba(0,0,0,0.4)",
  },
  centerWrap: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 24,
  },
  card: {
    width: "100%",
    backgroundColor: colors.white,
    borderRadius: 16,
    padding: 20,
    maxHeight: "80%",
  },
  closeBtn: {
    position: "absolute",
    right: 10,
    top: 10,
    padding: 8,
    zIndex: 1,
  },
  title: {
    marginBottom: 12,
  },
  warningBox: {
    backgroundColor: colors.lightYellow,
    borderRadius: 10,
    padding: 12,
  },
  warningTitle: {
    color: colors.errorRed,
    marginBottom: 6,
  },
  warningSub: {
    color: colors.mediumBlacklightBlack,
  },
  viewToggle: {
    paddingVertical: 12,
  },
  viewToggleText: {
    color: colors.plain_green,
  },
  list: {
    maxHeight: 220,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 10,
  },
  rowInfo: {
    flex: 1,
    marginRight: 12,
  },
  itemTitle: {
    color: colors.mediumBlack,
    marginBottom: 2,
  },
  orderLabel: {
    color: colors.mediumBlacklightBlack,
  },
  missingQty: {
    color: colors.errorRed,
  },
  separator: {
    height: StyleSheet.hairlineWidth,
    backgroundColor: colors.black02,
  },
  footer: {
    flexDirection: "row",
    gap: 10,
    marginTop: 16,
  },
  footerBtn: {
    flex: 1,
    height: 44,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  cancelBtn: {
    borderWidth: 1,
    borderColor: colors.lightMediumBlack,
    backgroundColor: colors.white,
  },
  confirmBtn: {
    backgroundColor: colors.plain_green,
  },
  cancelText: {
    color: colors.black,
  },
  confirmText: {
    color: colors.white,
  },
});

export default MissingItemsConfirmModal;
